import type { IIntegrationFetchEntry } from "@frontend-shared/types";
import {
  createIntegrationFetchReporter,
  pruneProcessedAttachmentIds,
} from "@frontend-shared/utils";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useChatContext } from "@/pages/chat/hooks/useChatContext";
import type { IAttachment } from "@/pages/chat/types";
import { EAttachmentStatus } from "@/pages/chat/types";

import type { IUploadFileParams } from "../types";
import { dataUrlToFile } from "../utils/data-url-to-file";

interface IUseIntegrationUploadProps {
  // NOTE: Returns whether the file was accepted. Hosts turn a file away with an
  // ordinary early return (an unsupported type, no model picked, a missing
  // organization uuid), and a rejected file must NOT count as queued — closing
  // the picker for one would leave the user with an error toast, a vanished row
  // and no progress entry to explain it (NSAI-5134).
  uploadFile: (props: IUploadFileParams) => boolean;
  // NOTE: NSAI-5132 — reports the files still being pulled out of a
  // third-party app so the caller can render them. The picker owns this phase
  // and the caller owns the S3 upload that follows, and without this bridge the
  // whole provider download — minutes for a video — happened behind a page
  // that showed nothing at all. Optional: callers that have no progress list
  // (the probe and presentation-theme dropzones) simply do not pass it.
  onFetchingFilesChange?: (entries: IIntegrationFetchEntry[]) => void;
  // NOTE: Fired once per batch after at least one integration file has been
  // ACCEPTED by the uploader. The picker has no state of its own for a queued
  // file — the attachment it renders from is dropped below — so the caller uses
  // this to close the picker and reveal the progress list underneath, which
  // NSAI-5132 made the home for both the fetch and the upload (NSAI-5134).
  onUploadQueued?: () => void;
}

// NOTE: The download already produced a real `File` (see useAttachmentDownload),
// so reuse it instead of decoding `content` again — re-decoding meant a second
// base64 pass over the whole payload on the main thread. The data-URL branch
// stays for attachments that only ever carried `content` (images, whose data
// URL is built for the preview). `null` means neither source is usable, which
// the caller reports rather than queueing an empty file.
const resolveAttachmentFile = (attachment: IAttachment): File | null => {
  if (attachment.file) {
    return attachment.file;
  }

  if (typeof attachment.content !== "string") {
    return null;
  }

  return dataUrlToFile({
    dataUrl: attachment.content,
    fileName: attachment.name,
    mimeType: attachment.type,
  });
};

export const useIntegrationUpload = ({
  uploadFile,
  onFetchingFilesChange,
  onUploadQueued,
}: IUseIntegrationUploadProps): void => {
  const { t } = useTranslation("uploads");
  const { attachments, setAttachments } = useChatContext();

  // NOTE: Hold `uploadFile` in a ref so the processing effect does NOT refire
  // when the caller passes a new function identity on each render. Callers
  // (e.g. ProbeFileUploadStep) often produce an unstable handler via
  // `useCallback([mutation, ...])`; without this ref, the effect refired on
  // every parent re-render and — combined with any React dev-double-render
  // quirk — re-triggered the upstream presign + S3 PUT in a loop before the
  // `setAttachments` removal below had a chance to commit (NSAI-0000).
  const uploadFileRef = useRef(uploadFile);
  useEffect(() => {
    uploadFileRef.current = uploadFile;
  }, [uploadFile]);

  // NOTE: Same reasoning as `uploadFileRef` — an unstable `onUploadQueued`
  // must not be able to retrigger the handoff effect.
  const onUploadQueuedRef = useRef(onUploadQueued);
  useEffect(() => {
    onUploadQueuedRef.current = onUploadQueued;
  }, [onUploadQueued]);

  // NOTE: Tracks attachment IDs we've already handed off to the uploader.
  // Prevents double-processing if React re-runs the effect (StrictMode,
  // re-entrant setState batching) before the `setAttachments` filter below
  // commits and removes the READY entry from state.
  const processedIdsRef = useRef<Set<string>>(new Set());

  // NOTE: Same ref treatment as `uploadFile` above — the caller typically
  // passes a bare `useState` setter, but nothing guarantees a stable identity,
  // and the reporting effect below must not refire on the caller's re-renders.
  const onFetchingFilesChangeRef = useRef(onFetchingFilesChange);
  useEffect(() => {
    onFetchingFilesChangeRef.current = onFetchingFilesChange;
  }, [onFetchingFilesChange]);

  // NOTE: NSAI-5132 — the reporter holds the last reported list as a value
  // signature, in `frontend-shared` so web and mobile report a download the
  // same way. Every progress tick replaces the whole `attachments` array, so
  // comparing identities would push a new list (and a caller re-render) even
  // when nothing a row displays has moved.
  const reportFetchingFilesRef = useRef(createIntegrationFetchReporter());

  useEffect(() => {
    reportFetchingFilesRef.current({
      attachments: attachments.filter(
        (attachment) => attachment.status === EAttachmentStatus.DOWNLOADING,
      ),
      onChange: (entries) => onFetchingFilesChangeRef.current?.(entries),
    });
  }, [attachments]);

  // NOTE: Returns whether the uploader took the file, which is what decides the
  // "queued" signal and nothing else — the attachment is dropped either way,
  // since a rejected file has already been reported to the user by the host and
  // re-offering it would just replay the rejection.
  const handleReadyAttachment = useCallback(
    (attachment: IAttachment): boolean => {
      try {
        const file = resolveAttachmentFile(attachment);
        if (!file) {
          toast.error(t("error.failed_to_read_integration_file"));
          return false;
        }

        return uploadFileRef.current({ file, transferId: attachment.id });
      } catch {
        toast.error(t("error.failed_to_read_integration_file"));
        return false;
      }
    },
    [t],
  );

  useEffect(() => {
    const readyAttachments = attachments.filter(
      (attachment) =>
        attachment.status === EAttachmentStatus.READY &&
        (!!attachment.file || typeof attachment.content === "string") &&
        !processedIdsRef.current.has(attachment.id),
    );

    if (readyAttachments.length === 0) {
      if (processedIdsRef.current.size > 0) {
        pruneProcessedAttachmentIds({
          processedIds: processedIdsRef.current,
          currentIds: new Set(attachments.map((att) => att.id)),
        });
      }
      return;
    }

    let hasQueuedUpload = false;
    for (const attachment of readyAttachments) {
      processedIdsRef.current.add(attachment.id);
      if (handleReadyAttachment(attachment)) {
        hasQueuedUpload = true;
      }
    }

    // NOTE: Remove all processed READY attachments in a single state update
    const readyIds = new Set(
      readyAttachments.map((attachment) => attachment.id),
    );
    setAttachments((prev) =>
      prev.filter((attachment) => !readyIds.has(attachment.id)),
    );

    if (hasQueuedUpload) {
      onUploadQueuedRef.current?.();
    }
  }, [attachments, setAttachments, handleReadyAttachment]);
};
